import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

/**
 * SearchableSelect Component
 * A searchable dropdown select component
 */
export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  getOptionLabel = (option) => option.label || option.name || String(option),
  getOptionValue = (option) => option.value || option.id || String(option),
  className = '',
  disabled = false,
  required = false,
  isDark = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const value = getOptionValue(option).toLowerCase();
      return label.includes(term) || value.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionValue]);

  // Get selected option
  const selectedOption = useMemo(() => {
    return options.find(opt => getOptionValue(opt) === value) || null;
  }, [options, value, getOptionValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option) => {
    const optionValue = getOptionValue(option);
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected Value Display */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${
          isDark
            ? 'bg-slate-950 border-slate-700 text-slate-200'
            : 'bg-white border-gray-300 text-gray-900'
        } ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
        }`}
      >
        <span className={`flex-1 text-left truncate ${
          selectedOption ? '' : (isDark ? 'text-slate-500' : 'text-gray-500')
        }`}>
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <div className="flex items-center gap-2 ml-2">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className={`p-0.5 rounded hover:bg-opacity-20 ${
                isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''} ${
              isDark ? 'text-slate-400' : 'text-gray-400'
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className={`absolute z-50 w-full mt-1 rounded-lg border shadow-xl ${
          isDark
            ? 'bg-slate-900 border-slate-700'
            : 'bg-white border-gray-200'
        }`}
        style={{ maxHeight: '300px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          {/* Search Input */}
          <div className={`p-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className="relative">
              <Search
                size={16}
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                  isDark ? 'text-slate-400' : 'text-gray-400'
                }`}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-blue-500 ${
                  isDark
                    ? 'bg-slate-950 border-slate-700 text-slate-200 placeholder-slate-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto" style={{ maxHeight: '250px' }}>
            {filteredOptions.length === 0 ? (
              <div className={`px-4 py-3 text-sm text-center ${
                isDark ? 'text-slate-400' : 'text-gray-500'
              }`}>
                No options found
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = optionValue === value;

                return (
                  <button
                    key={optionValue || index}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? isDark
                          ? 'bg-blue-600/20 text-blue-400'
                          : 'bg-blue-50 text-blue-600'
                        : isDark
                          ? 'text-slate-200 hover:bg-slate-800'
                          : 'text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {optionLabel}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Hidden input for form validation */}
      {required && (
        <input
          type="hidden"
          value={value || ''}
          required={required}
        />
      )}
    </div>
  );
}







